<!-- src/js/widgets/manager-requests/components/RequestsList.vue -->
<template>
  <div class="manager-requests__list-wrapper">
    <StatusFilterSimple v-model="selectedStatusLocal" />

    <div v-if="props.requests.length === 0 && !loading" class="manager-requests__list--empty">
      Нет поступивших заявок
    </div>

    <DataTable
      v-model:expandedRows="expandedRows"
      :loading="loading"
      :rows="10"
      :rowsPerPageOptions="[10, 25, 50]"
      :value="props.requests"
      @row-click="onRowClick"
      dataKey="id"
      paginator
      selectionMode="single"
      tableStyle="min-width: 70rem"
      v-else
    >
      <template #loading>
        <div class="manager-requests__loading">
          <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
          <span>Загрузка...</span>
        </div>
      </template>

      <!-- Колонка со шевроном -->
      <Column style="width: 3rem">
        <template #body="{ data }">
          <Button
            v-if="hasReplies(data)"
            :icon="getChevronIcon(data.id)"
            text
            @click.stop="toggleExpand(data.id)"
            class="p-0 manager-requests__expander-button"
          />
        </template>
      </Column>

      <Column header="№ заявки">
        <template #body="{ data }">
          <span :class="getRequestRowClass(data)"> #{{ data.id }} </span>
        </template>
      </Column>
      <Column header="Дата и время">
        <template #body="{ data }">
          <span :class="getRequestRowClass(data)">
            {{ formatDate(data.createdAt) }}
          </span>
        </template>
      </Column>
      <Column header="Компания">
        <template #body="{ data }">
          <span :class="getRequestRowClass(data)">
            {{ data.companyName || '' }}
          </span>
        </template>
      </Column>
      <Column header="Статус">
        <template #body="{ data }">
          <span :class="getStatusClass(data.status)">
            {{ data.status }}
          </span>
        </template>
      </Column>
      <Column header="Действия" style="width: 120px">
        <template #body="{ data }">
          <div class="manager-requests__actions">
            <Button
              icon="pi pi-reply"
              text
              @click.stop="emit('reply', data)"
              aria-label="Ответить"
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              @click.stop="$emit('delete-request', data.id)"
            />
          </div>
        </template>
      </Column>

      <!-- ЭКСПАНДЕР: структура как в основной таблице -->
      <template #expansion="{ data }">
        <div v-if="hasReplies(data)" class="manager-requests__replies-table">
          <div
            v-for="reply in data.replies"
            :key="reply.id"
            class="manager-requests__reply-row"
            @click="showReplyModal(reply.message)"
          >
            <div class="manager-requests__reply-cell-first"  style="width: 244px">
              <i class="pi pi-reply reply-icon" />
              <span>#{{ data.id }}</span>
            </div>
            <div class="manager-requests__reply-cell" style="width: 311px">
              {{ formatDate(data.createdAt) }}
            </div>
            <div class="manager-requests__reply-cell"  style="width: 385px">
              {{ data.companyName || '' }}
            </div>
            <div class="manager-requests__reply-cell" style="width: 127px">
              <span :class="getStatusClass(data.status)">
                {{ data.status }}
              </span>
            </div>
            <div class="manager-requests__reply-cell manager-requests__reply-actions">
            </div>
          </div>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="replyModalVisible"
      modal
      header="Ответ"
      :style="{ width: '90vw', maxWidth: '800px', height: 'auto' }"
    >
      <p>{{ selectedReplyMessage }}</p>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import type { Request, RequestStatus } from '../types';
import StatusFilterSimple from './StatusFilterSimple.vue';

const props = defineProps<{
  requests: Request[];
  loading: boolean;
  selectedStatus: RequestStatus | '';
}>();

const emit = defineEmits<{
  (e: 'update:selected-status', status: RequestStatus | ''): void;
  (e: 'row-click', request: Request): void;
  (e: 'delete-request', id: number): void;
  (e: 'reply', request: Request): void;
}>();

const expandedRows = ref<Record<number, boolean>>({});

const selectedStatusLocal = computed({
  get: () => props.selectedStatus,
  set: (value) => emit('update:selected-status', value),
});

watch(
  () => props.requests,
  (newRequests) => {
    // Раскрываем все заявки с ответами
    const newExpandedRows: Record<number, boolean> = {};
    newRequests.forEach((request) => {
      if (request.replies && request.replies.length > 0) {
        newExpandedRows[request.id] = true;
      }
    });
    expandedRows.value = newExpandedRows;
  },
  { immediate: true }
);

const replyModalVisible = ref(false);
const selectedReplyMessage = ref('');

function hasReplies(request: Request): boolean {
  return !!(request.replies && request.replies.length > 0);
}

function getChevronIcon(id: number): string {
  return expandedRows.value[id] ? 'pi pi-chevron-up' : 'pi pi-chevron-down';
}

function toggleExpand(id: number) {
  if (expandedRows.value[id]) {
    delete expandedRows.value[id];
  } else {
    expandedRows.value = { ...expandedRows.value, [id]: true };
  }
}

function showReplyModal(message: string) {
  selectedReplyMessage.value = message;
  replyModalVisible.value = true;
}

function onRowClick(event: any) {
  const data = event.data;
  emit('row-click', data);
}

function getRequestRowClass(request: Request) {
  const baseClass = 'manager-requests__table-row';
  const newStatusClass = request.status === 'Новая' ? ' manager-requests__new-status' : '';
  return baseClass + newStatusClass;
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString('ru-RU');
}

function getStatusClass(status: RequestStatus): string {
  return `manager-requests__status manager-requests__status--${status
    .toLowerCase()
    .replace(/\s+/g, '-')}`;
}
</script>
